import { CSSProperties } from 'react';

type StyleRecord<K extends string, T> = {
  [P in K]: T;
};

export type StyleConfig<T> = StyleRecord<
  keyof T & string,
  {
    [key: string]: any;
  }
>;

type Guide = Record<string, unknown> | (() => Record<string, unknown>);

export function makeStyles<K extends keyof CSSProperties, T extends Guide>(property: K, guide: T): StyleConfig<T> {
  const ret = {} as StyleConfig<T>;

  const hash = typeof guide === 'function' ? (guide as () => any)() : guide;

  for (const key in hash) {
    const cssObject = {
      [property]: hash[key],
    };

    ret[key as keyof typeof ret] = cssObject;
  }

  return ret;
}

export function makeComplexStyles<K extends keyof CSSProperties, T extends Guide>(
  properties: K[],
  guide: T,
): StyleConfig<T> {
  const ret = {} as StyleConfig<T>;
  const hash = typeof guide === 'function' ? (guide as () => any)() : guide;

  for (const key in hash) {
    const cssObject: Record<string, unknown> = {};

    properties.forEach(prop => {
      cssObject[prop] = hash[key];
    });

    ret[key as keyof typeof ret] = cssObject;
  }

  return ret;
}

export function makeCustomNestedStyle<K extends keyof CSSProperties, T extends Guide>(
  properties: K[],
  selector: string,
  guide: T,
): StyleConfig<T> {
  const hash = typeof guide === 'function' ? (guide as () => any)() : guide;

  return Object.entries(hash).reduce(
    (memo, [key, val]) => ({
      ...memo,
      [key.toString()]: {
        [selector]: properties.reduce(
          (props, prop) => ({
            ...props,
            [prop.toString()]: val,
          }),
          {},
        ),
      },
    }),
    {} as StyleConfig<T>,
  );
}
